import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  FileText,
  Search,
  Cable,
  FolderOpen,
  Plus,
  Play,
  AlertCircle,
  Route,
  Users,
  Settings,
  PenLine,
  User,
  ShieldAlert,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { features } from "@/lib/features";
import { PersonaAvatar } from "@/components/persona-identity";

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { data: projects } = trpc.project.list.useQuery(undefined, {
    staleTime: 30_000,
  });

  // Detect active project from pathname
  const activeProjectId = useMemo(() => {
    const match = location.pathname.match(/^\/projects\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const activeProject = useMemo(() => {
    if (!activeProjectId || !projects) return null;
    return projects.find((p) => p.id === activeProjectId) ?? null;
  }, [activeProjectId, projects]);

  const { data: outlines } = trpc.outline.list.useQuery(
    { projectId: activeProjectId! },
    { enabled: Boolean(activeProjectId && open), staleTime: 30_000 },
  );

  const { data: personas } = trpc.persona.list.useQuery(
    { projectId: activeProjectId! },
    { enabled: Boolean(activeProjectId && open), staleTime: 30_000 },
  );

  const { data: walkthroughs } = trpc.walkthrough.listByProject.useQuery(
    { projectId: activeProjectId! },
    { enabled: Boolean(activeProjectId && open), staleTime: 30_000 },
  );

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  function handleSelect(to: string) {
    navigate(to);
    setOpen(false);
  }

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command, project, or page..." />
      <CommandList className="max-h-[380px]">
        <CommandEmpty>No matching results.</CommandEmpty>

        {/* Current Project Actions (when inside a project) */}
        {activeProject && (
          <>
            <CommandGroup heading={`Current Project: ${activeProject.name}`}>
              <CommandItem
                onSelect={() => handleSelect(`/projects/${activeProject.id}`)}
              >
                <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Project Overview</span>
                <CommandShortcut>Overview</CommandShortcut>
              </CommandItem>
              {features.liveWalkthrough && (
                <CommandItem
                  onSelect={() =>
                    handleSelect(
                      `/projects/${activeProject.id}/live-walkthrough`,
                    )
                  }
                >
                  <Play
                    className="mr-2 h-4 w-4 text-emerald-500"
                    aria-hidden="true"
                  />
                  <span>Start Live Walkthrough</span>
                  <CommandShortcut>Action</CommandShortcut>
                </CommandItem>
              )}
              <CommandItem
                onSelect={() =>
                  handleSelect(`/projects/${activeProject.id}/findings`)
                }
              >
                <AlertCircle
                  className="mr-2 h-4 w-4 text-amber-500"
                  aria-hidden="true"
                />
                <span>Findings Triage Queue</span>
                <CommandShortcut>Triage</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  handleSelect(`/projects/${activeProject.id}/scenarios`)
                }
              >
                <Route className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Scenarios Catalog</span>
                <CommandShortcut>Catalog</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  handleSelect(`/projects/${activeProject.id}/personas`)
                }
              >
                <Users className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Personas Catalog</span>
                <CommandShortcut>Catalog</CommandShortcut>
              </CommandItem>
              <CommandItem
                onSelect={() =>
                  handleSelect(`/projects/${activeProject.id}/settings`)
                }
              >
                <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Project Settings</span>
                <CommandShortcut>Settings</CommandShortcut>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />

            {/* Scenarios */}
            {outlines && outlines.length > 0 && (
              <>
                <CommandGroup heading="Scenarios">
                  {outlines.slice(0, 10).map((sc) => (
                    <CommandItem
                      key={sc.id}
                      value={`scenario ${sc.name} ${sc.description || ""}`}
                      onSelect={() =>
                        handleSelect(
                          `/projects/${activeProject.id}/scenarios/${sc.id}`,
                        )
                      }
                    >
                      <Route
                        className="mr-2 h-4 w-4 text-primary"
                        aria-hidden="true"
                      />
                      <span className="truncate">{sc.name}</span>
                      <CommandShortcut>Scenario</CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Personas */}
            {personas && personas.length > 0 && (
              <>
                <CommandGroup heading="Personas">
                  {personas.slice(0, 10).map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`persona ${p.name} ${p.role || ""} ${p.context || ""}`}
                      onSelect={() =>
                        handleSelect(
                          `/projects/${activeProject.id}/personas/${p.id}`,
                        )
                      }
                    >
                      <PersonaAvatar
                        name={p.name}
                        size="xs"
                        className="mr-2 shrink-0"
                      />
                      <span className="truncate">
                        {p.name}
                        {p.role ? ` (${p.role})` : ""}
                      </span>
                      <CommandShortcut>Persona</CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Walkthrough Runs */}
            {walkthroughs && walkthroughs.length > 0 && (
              <>
                <CommandGroup heading="Walkthroughs">
                  {walkthroughs.slice(0, 8).map((w) => (
                    <CommandItem
                      key={w.id}
                      value={`walkthrough run ${w.scenarioName || "Evaluation"} ${w.personaName || ""}`}
                      onSelect={() =>
                        handleSelect(
                          `/projects/${activeProject.id}/walkthroughs/${w.id}`,
                        )
                      }
                    >
                      <Play
                        className="mr-2 h-4 w-4 text-emerald-500"
                        aria-hidden="true"
                      />
                      <span className="truncate">
                        {w.scenarioName || "Evaluation run"}
                        {w.personaName ? ` · ${w.personaName}` : ""}
                      </span>
                      <CommandShortcut>Run</CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
          </>
        )}

        {/* Quick Actions */}
        <CommandGroup heading="Quick Actions">
          <CommandItem onSelect={() => handleSelect("/projects/new")}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>Create new project</span>
            <CommandShortcut>New</CommandShortcut>
          </CommandItem>
          {features.knowledgeSources && (
            <>
              <CommandItem onSelect={() => handleSelect("/sources")}>
                <ShieldAlert className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Compliance sources hub</span>
                <CommandShortcut>Knowledge</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect("/search")}>
                <Search className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Semantic query playground</span>
                <CommandShortcut>Explore</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => handleSelect("/manual-entry")}>
                <PenLine className="mr-2 h-4 w-4" aria-hidden="true" />
                <span>Manual constraint entry</span>
                <CommandShortcut>Editor</CommandShortcut>
              </CommandItem>
            </>
          )}
        </CommandGroup>

        {/* Switch / Jump to Projects */}
        {projects && projects.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Projects">
              {projects.map((proj) => (
                <CommandItem
                  key={proj.id}
                  onSelect={() => handleSelect(`/projects/${proj.id}`)}
                >
                  <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span className="truncate">{proj.name}</span>
                  <CommandShortcut>Jump</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />

        {/* Main Navigation */}
        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => handleSelect("/projects")}>
            <FolderOpen className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>All Projects</span>
          </CommandItem>
          {features.knowledgeSources && (
            <CommandItem onSelect={() => handleSelect("/sources")}>
              <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>Knowledge Sources</span>
            </CommandItem>
          )}
          <CommandItem onSelect={() => handleSelect("/integration")}>
            <Cable className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>API & Agent Integration</span>
          </CommandItem>
          <CommandItem onSelect={() => handleSelect("/settings")}>
            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>Workspace Settings</span>
          </CommandItem>
          <CommandItem onSelect={() => handleSelect("/account")}>
            <User className="mr-2 h-4 w-4" aria-hidden="true" />
            <span>Account Profile</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
